/**
 * Carto Email Submission API Client
 * 
 * This file handles email submissions via Supabase Edge Functions.
 * No environment variables are needed - everything is handled server-side.
 * The API key below is public and safe to commit (just spam protection).
 */

import { logError, logEmailSubmissionAttempt, logEmailSubmissionSuccess } from './sentry';

// Email submission form types
export type EmailSubmissionFormSource = 'homepage_waitlist' | 'homepage_footer' | 'design_partner'

export interface EmailSubmission {
  id?: string
  email: string
  form_source: EmailSubmissionFormSource
  submitted_at?: string
  user_agent?: string
  created_at?: string
  updated_at?: string
}

// Carto project Edge Function endpoint
const EDGE_FUNCTION_URL = 'https://zplpvcxlfhhxfqwcgrno.supabase.co/functions/v1/save-carto-email-public';

// Public API key for spam protection (safe to commit to version control)
const PUBLIC_API_KEY = 'carto_public_2024';

// Carto project anon key (public and safe to commit)
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InpwbHB2Y3hsZmhoeGZxd2Nncm5vIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDg1NDEyNDYsImV4cCI6MjA2NDExNzI0Nn0.xdXO41c5LFt2aHUH6IKT2hnzY511q_lAbmJXRDKk4s0';

/**
 * Submit email via secure Edge Function
 * No environment variables required - all security handled server-side
 */
export const submitEmailToSupabase = async (
  email: string, 
  formSource: EmailSubmissionFormSource
): Promise<{ success: boolean; error?: string }> => {
  console.log(`📧 Attempting to submit email: ${email} from ${formSource}`);
  
  // Log email submission attempt to Sentry
  logEmailSubmissionAttempt(formSource, email);
  
  try {
    // Validate inputs
    if (!email || !formSource) {
      logError('submitEmailToSupabase', new Error('Missing required parameters'), { email, formSource });
      return { success: false, error: 'Missing email or form source' };
    }

    // Start both the API call and a minimum delay simultaneously
    const [response] = await Promise.all([
      fetch(EDGE_FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`, // Required by Supabase, but we validate with API key
        },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          form_source: formSource,
          user_agent: navigator.userAgent,
          api_key: PUBLIC_API_KEY
        })
      }),
      // 0.6 second delay for better UX
      new Promise(resolve => setTimeout(resolve, 600))
    ]);

    console.log('📊 API Response status:', response.status);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      logError('submitEmailToSupabase - API Error', new Error(`HTTP ${response.status}`), { 
        email, 
        formSource,
        status: response.status,
        errorData
      });
      return { success: false, error: errorData.error || `HTTP ${response.status}` };
    }

    const result = await response.json();
    console.log('📊 API Response:', result);

    if (!result.success) {
      logError('submitEmailToSupabase - Function Error', new Error(result.error), { 
        email, 
        formSource,
        result
      });
      return { success: false, error: result.error };
    }

    console.log('✅ Email submitted successfully');
    
    // Log successful email submission to Sentry
    logEmailSubmissionSuccess(formSource, email);
    
    return { success: true }
  } catch (error) {
    logError('submitEmailToSupabase - Catch Block', error, { 
      email, 
      formSource,
      errorType: typeof error,
      errorName: error instanceof Error ? error.name : 'Unknown'
    });
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Network error occurred' 
    }
  }
}

/**
 * Analytics function placeholder
 * Note: Analytics would need a separate secure Edge Function for production use
 */
export const getSubmissionAnalytics = async (): Promise<{
  success: boolean;
  data?: Array<{ form_source: string; count: number }>;
  error?: string;
}> => {
  // For now, return a placeholder since analytics would need admin access
  // In production, you'd create a separate Edge Function for analytics
  console.warn('Analytics function not implemented with Edge Functions. Create a separate admin endpoint.');
  return { 
    success: false, 
    error: 'Analytics requires admin access - implement via separate Edge Function' 
  };
} 